import {
  ArrowUUpLeft,
  BookOpenText,
  Briefcase,
  CalendarDots,
  CardsThree,
  Chat,
  Check,
  ClockCounterClockwise,
  ColumnsPlusRight,
  Compass,
  CreditCard,
  Envelope,
  EnvelopeSimple,
  EnvelopeSimpleOpen,
  Eye,
  FileArrowUp,
  Files,
  FunnelSimple,
  GearFine,
  GoogleChromeLogo,
  GraduationCap,
  type Icon,
  Image,
  LinkedinLogo,
  ListStar,
  MagnifyingGlass,
  MapPin,
  NoteBlank,
  PaintBrushBroad,
  PaperPlaneTilt,
  Tag,
  Translate,
  UserCircleDashed,
  UserList,
  Users,
  UsersFour,
  UsersThree,
  WebhooksLogo,
} from "@phosphor-icons/react";

import type { TagVariantType } from "common/components/ui/Tag";
import { tailwindTheme } from "common/helpers/utils";
import {
  FlowInmailTemplateSchema,
  FlowMessageTemplateSchema,
  FlowNoteTemplateSchema,
  InmailTemplateSchema,
  MessageTemplateSchema,
  NoteTemplateSchema,
} from "common/schemas";
import type {
  CampaignState,
  CampaignType,
  FacetParam,
  LeadsFacets,
  LeadsSearchParams,
  LinkedInProfile,
  Metrics,
  PaymentMethodTypes,
  SubscriptionStatus,
  SubscriptionType,
  TemplateType,
  UserPermissionsKey,
} from "common/types";

import type { FunctionComponent, SVGProps } from "react";
import type { AudienceFacets } from "../pages/campaigns/types";

import AmexIcon from "assets/icons/paymentCards/amex.svg?react";
import ApplePayIcon from "assets/icons/paymentCards/applePay.svg?react";
import DinersIcon from "assets/icons/paymentCards/dinnersClub.svg?react";
import DiscoverIcon from "assets/icons/paymentCards/discover.svg?react";
import JcbIcon from "assets/icons/paymentCards/jcb.svg?react";
import MastercardIcon from "assets/icons/paymentCards/mastercard.svg?react";
import PaypalIcon from "assets/icons/paymentCards/paypal.svg?react";
import VisaIcon from "assets/icons/paymentCards/visa.svg?react";
import AgencyGradientIcon from "assets/icons/plans/agency-gradient.svg?react";
import AgencyWhiteIcon from "assets/icons/plans/agency-white.svg?react";
import AvatarGradientIcon from "assets/icons/plans/avatar-gradient.svg?react";
import AvatarWhiteIcon from "assets/icons/plans/avatar-white.svg?react";
import OutreachGradientIcon from "assets/icons/plans/outreach-gradient.svg?react";
import OutreachWhiteIcon from "assets/icons/plans/outreach-white.svg?react";

export const CHROME_EXTENSION_URL = `https://chromewebstore.google.com/detail/aimfox-linkedin-session-m/${import.meta.env.VITE_CHROME_EXTENSION_ID}`;

export const CAMPAIGN_TYPES: Record<
  CampaignType,
  { icon: Icon; label: string }
> = {
  search: { icon: MagnifyingGlass, label: "LinkedIn search" },
  post: { icon: Image, label: "Post engagers" },
  event: { icon: CalendarDots, label: "Event attendees" },
  group: { icon: UsersFour, label: "Group members" },
  list: { icon: Files, label: "Custom list" },
  navigator: { icon: Compass, label: "Sales Navigator" },
  crm: { icon: FileArrowUp, label: "CRM" },
  drip: { icon: UsersThree, label: "Lead search" },
};
export const CAMPAIGN_STATES: Record<
  CampaignState,
  {
    label: string;
    tagVariant: TagVariantType;
    iconColor: string;
  }
> = {
  ACTIVE: {
    label: "Active",
    tagVariant: "success",
    iconColor: "fill-green-500",
  },
  CREATED: {
    label: "Ready",
    tagVariant: "orange",
    iconColor: "fill-orange-600",
  },
  PAUSED: {
    label: "Paused",
    tagVariant: "yellow",
    iconColor: "fill-yellow-500",
  },
  DONE: {
    label: "Completed",
    tagVariant: "primary",
    iconColor: "fill-purple-500",
  },
  INIT: {
    label: "Draft",
    tagVariant: "quaternary",
    iconColor: "fill-black-700",
  },
  SCHEDULED: {
    label: "Scheduled",
    tagVariant: "info",
    iconColor: "fill-blue-500",
  },
} as const;
export const TEMPLATE_TYPES: Record<
  TemplateType,
  {
    icon: Icon;
    label: string;
    shortLabel: string;
    validationSchema:
      | typeof InmailTemplateSchema
      | typeof NoteTemplateSchema
      | typeof MessageTemplateSchema;
    flowValidationSchema:
      | typeof FlowInmailTemplateSchema
      | typeof FlowNoteTemplateSchema
      | typeof FlowMessageTemplateSchema;
  }
> = {
  NOTE_TEMPLATE: {
    label: "Connection note",
    shortLabel: "Note",
    icon: NoteBlank,
    validationSchema: NoteTemplateSchema,
    flowValidationSchema: FlowNoteTemplateSchema,
  },
  MESSAGE_TEMPLATE: {
    label: "Direct message",
    shortLabel: "Message",
    icon: Chat,
    validationSchema: MessageTemplateSchema,
    flowValidationSchema: FlowMessageTemplateSchema,
  },
  INMAIL_TEMPLATE: {
    label: "Free InMail",
    shortLabel: "InMail",
    icon: EnvelopeSimple,
    validationSchema: InmailTemplateSchema,
    flowValidationSchema: FlowInmailTemplateSchema,
  },
} as const;
export const SUBSCRIPTION_STATUSES: Record<SubscriptionStatus, string> = {
  in_trial: "Trial",
  active: "Active",
  non_renewing: "Non Renewing",
  cancelled: "Cancelled",
} as const;

export const ALL_METRICS: {
  [key in keyof Metrics]: {
    label: string;
    tooltipText: string;
    icon: Icon;
    className: { icon: string; stroke: string; hover: string };
    selectedClassName: { button: string };
    gradientColor: string;
  };
} = {
  views: {
    label: "Profile Views",
    tooltipText:
      "The number of LinkedIn accounts you viewed as part of Aimfox campaigns",
    icon: Eye,
    className: {
      hover: "hover:bg-orange-100",
      icon: "text-orange-600",
      stroke: "stroke-orange-600",
    },
    selectedClassName: { button: "bg-orange-300" },
    gradientColor: tailwindTheme.colors.orange["300"],
  },
  sent_connections: {
    label: "Invites",
    tooltipText:
      "The number of connection invites you sent with Aimfox. This includes both invites with and without a note",
    icon: EnvelopeSimpleOpen,
    className: {
      hover: "hover:bg-magenta-100",
      icon: "text-magenta-600",
      stroke: "stroke-magenta-600",
    },
    selectedClassName: { button: "bg-magenta-300" },
    gradientColor: tailwindTheme.colors.magenta["300"],
  },
  accepted_connections: {
    label: "Accepted",
    tooltipText:
      "How many of your connection invites were accepted. Everyone who accepts your connection request is considered a lead",
    icon: Check,
    className: {
      hover: "hover:bg-green-100",
      icon: "text-green-600",
      stroke: "stroke-green-600",
    },
    selectedClassName: { button: "bg-green-300" },
    gradientColor: tailwindTheme.colors.green["300"],
  },
  message_requests: {
    label: "Message Requests",
    tooltipText:
      "The number of message requests you sent with Aimfox campaigns. Message requests are initial messages sent in group message and event message campaigns",
    icon: PaperPlaneTilt,
    className: {
      hover: "hover:bg-black-100",
      icon: "text-black-950",
      stroke: "stroke-black-950",
    },
    selectedClassName: { button: "bg-black-300" },
    gradientColor: "",
  },
  sent_messages: {
    label: "Messages",
    tooltipText:
      "The number of direct messages you sent with Aimfox. This stat shows your follow-up messages",
    icon: Chat,
    className: {
      hover: "hover:bg-blue-100",
      icon: "text-blue-500",
      stroke: "stroke-blue-500",
    },
    selectedClassName: { button: "bg-blue-300" },
    gradientColor: tailwindTheme.colors.blue["300"],
  },
  sent_inmails: {
    label: "InMails",
    tooltipText:
      "How many free inmails you sent out with Aimfox. We do not count free, or other inmails you sent out manually",
    icon: Envelope,
    className: {
      hover: "hover:bg-purple-100",
      icon: "text-purple-600",
      stroke: "stroke-purple-600",
    },
    selectedClassName: { button: "bg-purple-300" },
    gradientColor: tailwindTheme.colors.purple["300"],
  },
  replies: {
    label: "Replies",
    tooltipText:
      "How many replies you received to your inmails, messages and message requests",
    icon: ArrowUUpLeft,
    className: {
      hover: "hover:bg-yellow-100",
      icon: "text-yellow-600",
      stroke: "stroke-yellow-600",
    },
    selectedClassName: { button: "bg-yellow-300" },
    gradientColor: tailwindTheme.colors.yellow["300"],
  },
};
export const PAYMENT_METHODS: Record<
  PaymentMethodTypes,
  {
    label: string;
    name: "card" | "paypal" | "apple-pay";
    icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  }
> = {
  card: { label: "Credit Card", name: "card" },
  paypal_express_checkout: {
    label: "PayPal",
    name: "paypal",
    icon: PaypalIcon,
  },
  apple_pay: { label: "Apple Pay", name: "apple-pay", icon: ApplePayIcon },
};
export const CARD_ICONS: {
  [cardType: string]: FunctionComponent<SVGProps<SVGSVGElement>>;
} = {
  visa: VisaIcon,
  jcb: JcbIcon,
  american_express: AmexIcon,
  discover: DiscoverIcon,
  diners_club: DinersIcon,
  mastercard: MastercardIcon,
};

export const USER_PERMISSION_LEVELS = {
  full: {
    label: "Full access",
    permissionName: "full",
    tagVariant: "secondary",
  },
  limited: {
    label: "Limited access",
    permissionName: "limited",
    tagVariant: "tertiary",
  },
  none: {
    label: "No access",
    permissionName: "",
    tagVariant: "quaternary",
  },
} as const;

export const USER_PERMISSIONS_CATEGORIES: Record<
  UserPermissionsKey,
  {
    label: string;
    icon: Icon;
    permissionOptions: {
      description: string;
      level: keyof typeof USER_PERMISSION_LEVELS;
      label?: string;
    }[];
  }
> = {
  campaigns: {
    label: "Campaigns",
    icon: ColumnsPlusRight,
    permissionOptions: [
      {
        description: "User has no access to the Campaigns page.",
        level: "none",
      },
      {
        description:
          "User can view created campaigns, but cannot make any changes.",
        level: "limited",
      },
      {
        description:
          "User has full access to campaigns, for all added LinkedIn accounts.",
        level: "full",
      },
    ],
  },
  leads: {
    label: "Leads",
    icon: UsersThree,
    permissionOptions: [
      {
        description:
          "User has no access to the Leads page. They cannot edit Labels, Favorite leads, or Notes.",
        level: "none",
      },
      {
        description:
          "User has full access to the Leads page, Labels, Favorite leads, and Notes.",
        level: "full",
      },
    ],
  },
  inbox: {
    label: "Inbox",
    icon: Envelope,
    permissionOptions: [
      {
        description:
          "User has no access to Inbox. They cannot use “Message Lead” feature from Leads.",
        level: "none",
      },
      {
        description:
          "User can only manage conversations started from Aimfox campaigns. Private conversations are hidden.",
        level: "limited",
      },
      {
        description:
          "User has full access to Inbox. They can see all conversations.",
        level: "full",
      },
    ],
  },
  accounts: {
    label: "Accounts",
    icon: UserList,
    permissionOptions: [
      {
        description: "User cannot see account settings, or manage accounts.",
        level: "none",
      },
      {
        description:
          "User can view workspace account settings and added accounts, but cannot manage accounts.",
        level: "limited",
      },
      {
        description: "User has full access to account settings.",
        level: "full",
      },
    ],
  },
  templates: {
    label: "Templates",
    icon: CardsThree,
    permissionOptions: [
      {
        description: "User has no access to the Templates page.",
        level: "none",
      },
      {
        description: "User can view templates, but cannot make any edits.",
        level: "limited",
      },
      {
        description: "User has full access to templates.",
        level: "full",
      },
    ],
  },
  members: {
    label: "Members",
    icon: Users,
    permissionOptions: [
      {
        description:
          "User has no access to members page in workspace settings.",
        level: "none",
      },
      {
        description: "User can view workspace members, but cannot manage them.",
        level: "limited",
      },
      {
        description:
          "User has full access to the workspace members page. They can change roles, invite and remove members.",
        level: "full",
      },
    ],
  },
  billing: {
    label: "Billing",
    icon: CreditCard,
    permissionOptions: [
      {
        description:
          "User cannot make any changes to the subscription, and has no access to invoices.",
        level: "none",
      },
      {
        description:
          "User can view and download invoices, but cannot make any changes to subscription or billing.",
        level: "limited",
      },
      {
        description:
          "User can add or remove seats, cancel or reactivate the subscription, and has full access to invoices.",
        level: "full",
      },
    ],
  },
  integrations: {
    label: "Integrations",
    icon: GoogleChromeLogo,
    permissionOptions: [
      {
        description:
          "User cannot access the Workspace settings Integrations page.",
        level: "none",
      },
      {
        description:
          "User has full access to add, remove and manage workspace integrations.",
        level: "full",
      },
    ],
  },
  public_api: {
    label: "API",
    icon: GearFine,
    permissionOptions: [
      {
        description: "User cannot access the Workspace settings API page.",
        level: "none",
      },
      {
        description:
          "User has full access to add, remove and manage workspace API keys.",
        level: "full",
      },
    ],
  },
  webhooks: {
    label: "Webhooks",
    icon: WebhooksLogo,
    permissionOptions: [
      {
        label: "No access",
        description:
          "User has no access to the workspace settings Webhooks page.",
        level: "none",
      },
      {
        label: "Full access",
        description:
          "User has full access to add, remove and manage workspace webhooks.",
        level: "full",
      },
    ],
  },
  branding: { label: "Branding", icon: PaintBrushBroad, permissionOptions: [] },
  clients: { label: "Client", icon: UserCircleDashed, permissionOptions: [] },
} as const;

export const LEADS_FACETS: Record<
  keyof (AudienceFacets & LeadsFacets),
  {
    label: string;
    icon: Icon;
  }
> = {
  locations: {
    label: "Locations",
    icon: MapPin,
  },
  current_companies: {
    label: "Current companies",
    icon: Briefcase,
  },
  past_companies: {
    label: "Past companies",
    icon: ClockCounterClockwise,
  },
  schools: {
    label: "Education",
    icon: GraduationCap,
  },
  origins: {
    label: "Origins",
    icon: FunnelSimple,
  },
  labels: {
    label: "Labels",
    icon: Tag,
  },
  skills: {
    label: "Skills",
    icon: BookOpenText,
  },
  interests: {
    label: "Interests",
    icon: ListStar,
  },
  languages: {
    label: "Languages",
    icon: Translate,
  },
  lead_of: { label: "Accounts", icon: UserList },
  premium: { label: "Premium", icon: LinkedinLogo },
};
export const DEFAULT_LEADS_SEARCH_PARAMS: LeadsSearchParams<FacetParam> = {
  keywords: "",
  favorite: false,
  origins: [],
  past_companies: [],
  current_companies: [],
  locations: [],
  labels: [],
  interests: [],
  skills: [],
  education: [],
  languages: [],
  lead_of: [],
};
export const PAGE_SIZE_OPTIONS = [5, 8, 10, 15, 20];

export const TEMPLATE_PREVIEW_ACCOUNTS: LinkedInProfile[] = [
  {
    email: "",
    first_name: "Aleksandar",
    last_name: "Besarovic",
    id: "350311515",
    full_name: "Aleksandar Besarovic",
    public_identifier: "aleksandar-besarovic",
    picture_url:
      "https://cdn.constel.co/linkedin-profile/ACoAABThVFsBoJzUUWcsZdWPXIoL8U3iyMOnsqE.jpg",
    occupation: "CEO at Aimfox.com",
    urn: "ACoAABThVFsBoJzUUWcsZdWPXIoL8U3iyMOnsqE",
    location: {
      name: "Serbia",
      urn: "101855366",
    },
    premium: false,
  },
  {
    email: "",
    first_name: "Richard",
    last_name: "Branson",
    id: "204068115",
    full_name: "Richard Branson",
    public_identifier: "rbranson",
    picture_url:
      "https://cdn.constel.co/linkedin-profile/ACoAAAwp1RMBxG26M3Qkhm4klqLNr1bIplo6GhY.jpg",
    occupation: "Founder at Virgin Group",
    urn: "ACoAAAwp1RMBxG26M3Qkhm4klqLNr1bIplo6GhY",
    premium: false,
  },
  {
    email: "",
    first_name: "Bill",
    last_name: "Gates",
    id: "251749025",
    full_name: "Bill Gates",
    public_identifier: "williamhgates",
    picture_url:
      "https://cdn.constel.co/linkedin-profile/ACoAAA8BYqEBCGLg_vT_ca6mMEqkpp9nVffJ3hc.jpg",
    occupation: "Chair, Gates Foundation and Founder, Breakthrough Energy",
    urn: "ACoAAA8BYqEBCGLg_vT_ca6mMEqkpp9nVffJ3hc",
    location: {
      name: "Seattle, Washington, United States",
      urn: "104116203",
    },
    premium: false,
  },
];

export const PRICING_PLANS: Record<
  SubscriptionType,
  {
    name: string;
    description: string;
    altDescription: string;
    icon: FunctionComponent<SVGProps<SVGSVGElement>>;
    gradientIcon: FunctionComponent<SVGProps<SVGSVGElement>>;
  }
> = {
  user: {
    name: "Outreach Seat",
    description: "Grow your LinkedIn network with fully automated campaigns",
    altDescription:
      "Connect your LinkedIn profiles to Aimfox and start your outbound",
    icon: OutreachWhiteIcon,
    gradientIcon: OutreachGradientIcon,
  },
  rental: {
    name: "Outreach Avatar",
    description: "Scale your LinkedIn outreach with rented accounts",
    altDescription:
      "Rent and brand ready-to-go LinkedIn profiles. Scale your campaigns today!",
    icon: AvatarWhiteIcon,
    gradientIcon: AvatarGradientIcon,
  },
  agency: {
    name: "Agency Plan",
    description: "Manage your clients' profiles across multiple workspaces",
    altDescription: "Manage your clients' accounts across multiple workspaces",
    icon: AgencyWhiteIcon,
    gradientIcon: AgencyGradientIcon,
  },
} as const;
